import { motion } from "framer-motion";

const LEGS_WITH_SHOES = (walking) =>
  walking ? (
    <>
      <motion.g
        style={{ transformBox: "fill-box", transformOrigin: "top center" }}
        animate={{ rotate: [14, -14, 14] }}
        transition={{ repeat: Infinity, duration: 0.7, ease: "easeInOut" }}
      >
        <path d="M50 140 L46 166" stroke="#1E5B3C" strokeWidth="9" strokeLinecap="round" />
        <path d="M46 166 Q37 173 34 163 Q42 165 47 159 Z" fill="#A31621" />
        <circle cx="34" cy="162" r="2.6" fill="#D4AF37" />
      </motion.g>
      <motion.g
        style={{ transformBox: "fill-box", transformOrigin: "top center" }}
        animate={{ rotate: [-14, 14, -14] }}
        transition={{ repeat: Infinity, duration: 0.7, ease: "easeInOut" }}
      >
        <path d="M70 140 L74 166" stroke="#1E5B3C" strokeWidth="9" strokeLinecap="round" />
        <path d="M74 166 Q83 173 86 163 Q78 165 73 159 Z" fill="#A31621" />
        <circle cx="86" cy="162" r="2.6" fill="#D4AF37" />
      </motion.g>
    </>
  ) : (
    <>
      <path d="M50 140 L47 166" stroke="#1E5B3C" strokeWidth="9" strokeLinecap="round" />
      <path d="M47 166 Q38 173 35 163 Q43 165 48 159 Z" fill="#A31621" />
      <circle cx="35" cy="162" r="2.6" fill="#D4AF37" />
      <path d="M70 140 L73 166" stroke="#1E5B3C" strokeWidth="9" strokeLinecap="round" />
      <path d="M73 166 Q82 173 85 163 Q77 165 72 159 Z" fill="#A31621" />
      <circle cx="85" cy="162" r="2.6" fill="#D4AF37" />
    </>
  );

export const ElfFigure = ({ flip = false, legs = false, walking = false }) => (
  <svg
    viewBox={legs ? "0 0 120 185" : "0 0 120 150"}
    fill="none"
    className="h-auto w-full drop-shadow-[0_10px_18px_rgba(0,0,0,0.45)]"
    style={flip ? { transform: "scaleX(-1)" } : undefined}
  >
    {/* ears */}
    <ellipse cx="26" cy="74" rx="10" ry="6" fill="#F2C9A0" transform="rotate(-18 26 74)" />
    <ellipse cx="94" cy="74" rx="10" ry="6" fill="#F2C9A0" transform="rotate(18 94 74)" />
    {/* body */}
    <path d="M36 104 Q60 90 84 104 L90 142 Q60 154 30 142 Z" fill="#1E5B3C" />
    {/* collar */}
    <path d="M38 102 L48 111 L56 100 L64 111 L72 100 L82 109 Q60 95 38 102 Z" fill="#F4E9D0" />
    {/* gold buttons */}
    <circle cx="60" cy="120" r="2.6" fill="#D4AF37" />
    <circle cx="60" cy="133" r="2.6" fill="#D4AF37" />
    {/* face */}
    <circle cx="60" cy="74" r="28" fill="#F7D7B0" />
    {/* hat */}
    <path d="M28 62 Q56 16 92 28 Q85 42 90 54 Q60 40 30 64 Z" fill="#A31621" />
    <path d="M27 61 Q60 43 93 53 Q93 61 88 62 Q60 50 33 66 Q27 67 27 61 Z" fill="#F4E9D0" />
    <circle cx="93" cy="27" r="6.5" fill="#D4AF37" />
    {/* eyes */}
    <circle cx="49" cy="72" r="3.2" fill="#2B1B12" />
    <circle cx="71" cy="72" r="3.2" fill="#2B1B12" />
    <circle cx="50" cy="71" r="1" fill="#FFF" />
    <circle cx="72" cy="71" r="1" fill="#FFF" />
    {/* cheeks + smile */}
    <circle cx="42" cy="81" r="4.5" fill="#E88A7A" opacity="0.55" />
    <circle cx="78" cy="81" r="4.5" fill="#E88A7A" opacity="0.55" />
    <path d="M52 83 Q60 90 68 83" stroke="#8A4B2A" strokeWidth="2.4" strokeLinecap="round" />
    {/* left arm resting with red mitten */}
    <path d="M36 108 Q23 114 20 126" stroke="#1E5B3C" strokeWidth="10" strokeLinecap="round" />
    <circle cx="25" cy="118" r="4" fill="#F4E9D0" />
    <circle cx="19" cy="129" r="7" fill="#A31621" />
    {/* waving arm with red mitten */}
    <motion.g
      style={{ transformBox: "fill-box", transformOrigin: "bottom center" }}
      animate={{ rotate: [0, -20, 0] }}
      transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
    >
      <path d="M85 110 Q101 102 105 86" stroke="#1E5B3C" strokeWidth="10" strokeLinecap="round" />
      <circle cx="103" cy="91" r="4" fill="#F4E9D0" />
      <circle cx="107" cy="82" r="7.5" fill="#A31621" />
    </motion.g>
    {/* legs with curled shoes */}
    {legs && LEGS_WITH_SHOES(walking)}
  </svg>
);

const VARIANTS = {
  bob: { animate: { y: [0, -9, 0], rotate: [0, -2.5, 0, 2.5, 0] }, duration: 3.4 },
  peek: { animate: { x: [0, -12, 0], rotate: [5, 9, 5] }, duration: 4.2 },
  jump: { animate: { y: [0, -16, 0], rotate: [0, -4, 0] }, duration: 1.9 },
  walk: { animate: { y: [0, -3, 0] }, duration: 0.7 },
  still: { animate: {}, duration: 1 },
};

export const Elf = ({ variant = "bob", className = "", flip = false, legs, ...rest }) => {
  const config = VARIANTS[variant] ?? VARIANTS.bob;
  const showLegs = legs ?? (variant === "walk" || variant === "jump");
  return (
    <motion.div
      aria-hidden
      className={`pointer-events-none select-none ${className}`}
      animate={config.animate}
      transition={{ repeat: Infinity, duration: config.duration, ease: "easeInOut" }}
      {...rest}
    >
      <ElfFigure flip={flip} legs={showLegs} walking={variant === "walk"} />
    </motion.div>
  );
};
